import { Command } from 'cmdk'
import {
  Text,
  Badge,
  GreyGrey60,
  GreyGrey80,
  Primary13Regular,
  Primary13SemiBold,
  Box
} from '@sequencehq/core-components'
import { toInvoiceStatusBadgeProps, toMoney } from '@sequencehq/utils'
import type { Currency } from '@sequencehq/api'
import { format, parseISO } from 'date-fns'
import { CustomerLogoById } from 'Customer/index'
import { useInvoiceDueDateFormatter } from 'Invoices/hooks/useInvoiceDueDateFormatter'
import { IconSlot } from '../components/IconSlot'
import { SEARCH_STALE_TIME } from '../globalSearch.constants'
import type { InvoiceResult } from '../useGlobalSearchResults'

type InvoiceResultsProps = {
  invoices: InvoiceResult[]
  isActive: boolean
  onNavigate: (path: string) => void
}

export const InvoiceResults = ({
  invoices,
  isActive,
  onNavigate
}: InvoiceResultsProps) => {
  const formatDueDate = useInvoiceDueDateFormatter()

  if (!isActive || invoices.length === 0) {
    return null
  }

  return (
    <Command.Group heading="Invoices">
      {invoices.map(invoice => {
        const dueDateText = formatDueDate({
          dueDate: invoice.dueDate,
          formatDate: dueDate => format(parseISO(dueDate), 'MMM d')
        })

        return (
          <Command.Item
            key={invoice.id}
            value={`invoice-${invoice.id}`}
            onSelect={() => onNavigate(`/invoices/${invoice.id}`)}
            data-testid="global-search.invoice-result"
          >
            <IconSlot>
              <CustomerLogoById
                customerId={invoice.customerId}
                staleTime={SEARCH_STALE_TIME}
              />
            </IconSlot>
            <Text
              {...Primary13SemiBold}
              color={GreyGrey80}
              whiteSpace="nowrap"
              flexShrink={0}
            >
              {invoice.invoiceNumber ?? 'Draft'}
            </Text>
            {invoice.grossTotal && invoice.currency && (
              <Text
                {...Primary13Regular}
                color={GreyGrey80}
                whiteSpace="nowrap"
                flexShrink={0}
              >
                {toMoney({
                  value: invoice.grossTotal,
                  currency: invoice.currency as Currency
                })}
              </Text>
            )}
            <Box flex="1" />
            <Badge
              {...toInvoiceStatusBadgeProps({
                status: invoice.status,
                size: 'sm'
              })}
            />
            {dueDateText && (
              <Text
                {...Primary13Regular}
                color={GreyGrey60}
                whiteSpace="nowrap"
                flexShrink={0}
                data-testid={`global-search.invoice-result.${invoice.id}.dueDate`}
              >
                {dueDateText}
              </Text>
            )}
          </Command.Item>
        )
      })}
    </Command.Group>
  )
}
